import { useEffect, useRef } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

import ChatMessageList from '../../components/chat/chat-message-list';
import ChatComposer from '../../components/chat/chat-composer';
import type { MessageInputHandle } from '../../components/chat/message-input';

import { useMarkChat } from '../../hooks/use-mark-chat';
import { useMarkReadOnly } from '../../hooks/use-mark-read-only';

import { useMarkPanelActions } from '../mark-panel-context';
import { useMarkPanelTitle } from '../hooks/use-mark-panel-title';
import type { MarkPanelSession } from '../mark-panel.types';

import MarkPanelDrawer from './mark-panel-drawer';
import MarkContextChip from './mark-context-chip';
import MarkPromptList from './mark-prompt-list';

const CHAT_CONTENT_SX = { width: '100%' } satisfies SxProps<Theme>;

export interface MarkPanelChatProps {
  session: MarkPanelSession;
  onClose: () => void;
}

const MarkPanelChat = ({ session, onClose }: MarkPanelChatProps) => {
  const actions = useMarkPanelActions();
  const isReadOnly = useMarkReadOnly();
  const title = useMarkPanelTitle(session.conversationId, session.clientId);
  const inputRef = useRef<MessageInputHandle>(null);

  const {
    oktaUserId,
    messages,
    isInputDisabled,
    isBlocked,
    limitReachedMessage,
    activeInputPart,
    sendMessage,
    retryMessage,
    regenerateMessage,
    editingMessage,
    startEditingMessage,
    cancelEditingMessage,
    submitEditedMessage,
    submitInputPart,
    dismissInputPart,
    messagesEndRef,
  } = useMarkChat({
    conversationId: session.conversationId,
    clientId: session.clientId,
    isReadOnly,
    onConversationCreated: (conversationId) =>
      actions?.bindConversation(session.key, conversationId),
    analyticsSurface: session.surfaceId,
    analyticsContext: {
      contextKind: session.context.kind,
      contextSection: session.context.section,
    },
    buildMessageRequest: (text) => {
      const pageContext = actions?.buildPageContext() ?? null;

      return pageContext
        ? { requestMessage: text, pageContext }
        : { requestMessage: text };
    },
  });

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  if (!actions) return null;

  return (
    <MarkPanelDrawer
      title={title}
      onClose={onClose}
      footer={
        <ChatComposer
          isBlocked={isBlocked}
          limitReachedMessage={limitReachedMessage}
          activeInputPart={activeInputPart}
          onSubmitInputPart={submitInputPart}
          onDismissInputPart={dismissInputPart}
          onSendMessage={(text) => sendMessage(text, 'custom')}
          isInputDisabled={isInputDisabled}
          editingMessage={editingMessage}
          onSubmitEditedMessage={submitEditedMessage}
          onCancelEditingMessage={cancelEditingMessage}
          inputRef={inputRef}
        />
      }
    >
      <MarkContextChip context={session.context} />

      {messages.length ? (
        <ChatMessageList
          messages={messages}
          oktaUserId={oktaUserId}
          clientId={session.clientId}
          conversationId={session.conversationId}
          isReadOnly={isReadOnly}
          isActionDisabled={isBlocked}
          isEditDisabled={isInputDisabled}
          onRetry={retryMessage}
          onRegenerate={regenerateMessage}
          onEditMessage={startEditingMessage}
          messagesEndRef={messagesEndRef}
          contentSx={CHAT_CONTENT_SX}
        />
      ) : (
        <MarkPromptList
          prompts={session.context.prompts}
          onSelect={(prompt) => sendMessage(prompt.text, 'predefined')}
          isDisabled={isBlocked || isReadOnly}
        />
      )}
    </MarkPanelDrawer>
  );
};

export default MarkPanelChat;
